import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { useQuery } from "urql";
import { Granularity as GqlGranularity, type KpiComparisonMode } from "@acto/gql/graphql";
import { Kpi as DomainKpi } from "@acto/taskpane/core/domain";
import { fromGqlKpi, toGqlKpi } from "./kpiTransformers";

const ProductGroupKpiTotalsQuery = graphql(`
  query GetProductGroupKpiTotals(
    $clientId: ID!
    $productGroupId: ID!
    $kpis: [Kpi!]!
    $dateRange: DateRangeInput
    $granularity: Granularity!
    $comparisonMode: KpiComparisonMode
  ) {
    kpiSummaries(
      primaryClientId: $clientId
      productGroupId: $productGroupId
      kpis: $kpis
      dateRange: $dateRange
      granularity: $granularity
      comparisonMode: $comparisonMode
    ) {
      values {
        kpi
        totalValue
        comparison {
          totalValue
          change {
            percentageChange
          }
          range {
            startDate
            endDate
          }
        }
      }
    }
  }
`);

export interface ProductGroupKpiTotal {
  value: number;
  comparison: {
    value: number;
    percentageChange: number | null;
    range: { startDate: string; endDate: string };
  } | null;
}

interface UseProductGroupKpiTotalsOptions {
  clientId: string;
  productGroupId: string;
  kpis: DomainKpi[];
  dateRange: { startDate: string; endDate: string };
  comparisonMode: KpiComparisonMode;
}

/** `productGroupId` may be a node at any depth of the product tree, not only a top-level group. */
export function useProductGroupKpiTotals({
  clientId,
  productGroupId,
  kpis,
  dateRange,
  comparisonMode,
}: UseProductGroupKpiTotalsOptions) {
  const [result] = useQuery({
    query: ProductGroupKpiTotalsQuery,
    variables: {
      clientId,
      productGroupId,
      kpis: kpis.map(toGqlKpi),
      dateRange,
      granularity: GqlGranularity.Monthly,
      comparisonMode,
    },
    requestPolicy: "cache-first",
  });

  const totals = useMemo(() => {
    const byKpi = new Map<DomainKpi, ProductGroupKpiTotal>();
    for (const value of result.data?.kpiSummaries.values ?? []) {
      byKpi.set(fromGqlKpi(value.kpi), {
        value: value.totalValue,
        comparison: value.comparison
          ? {
              value: value.comparison.totalValue,
              percentageChange: value.comparison.change.percentageChange ?? null,
              range: value.comparison.range,
            }
          : null,
      });
    }
    return byKpi;
  }, [result.data]);

  return {
    totals,
    // urql serves the previous variables' data while refetching, so stale totals belong to another product node.
    isLoading: result.fetching || result.stale,
    error: result.error,
  };
}
